import { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CheckIcon from "@mui/icons-material/Check";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import { THEMES, THEME_ORDER, type ColorMode, type ModuleKey, type ThemeId } from "@/theme";
import { useColorMode, useResolvedColorMode, type ColorModePreference } from "@/store/colorMode";
import { useDensity } from "@/store/density";
import { useEffectiveThemeId, useThemePref } from "@/store/theme";
import type { Density } from "@/theme";

/**
 * Appearance controls — theme, light/dark, and row density in one popover.
 *
 * Replaces the two separate topbar icon buttons. Everything applies instantly: there is no Save,
 * because the change *is* the preview, and a settings panel you have to confirm makes people
 * hesitate to try the options at all.
 */

/**
 * A miniature of the app in a given theme: sidebar band on the left, page canvas behind, accent
 * dot on top. Three colours is enough to recognise a theme at a glance, which is the point — the
 * names ("Teal", "Cyan") do not distinguish neighbouring hues nearly as well as seeing them.
 *
 * These are literal palette values rather than theme tokens on purpose: the tile has to show a
 * theme that is *not* the active one.
 */
function ThemeTile({ themeId, mode, selected }: { themeId: ThemeId; mode: ColorMode; selected: boolean }) {
  const def = THEMES[themeId];
  return (
    <Box
      sx={{
        position: "relative",
        width: 52,
        height: 38,
        borderRadius: 1.5,
        overflow: "hidden",
        display: "flex",
        backgroundColor: def.neutral[mode].background,
        outline: (t) => (selected ? `2px solid ${t.palette.primary.main}` : `1px solid ${t.palette.divider}`),
        outlineOffset: selected ? 1 : 0,
      }}
    >
      <Box sx={{ width: 13, backgroundColor: def.shell[mode].bg }} />
      <Box sx={{ flex: 1, position: "relative" }}>
        {def.moduleAccents ? (
          // The departmental theme has no single accent to show, so the tile shows that it has many.
          <Box sx={{ position: "absolute", left: 6, top: 8, display: "flex", gap: 0.4, flexWrap: "wrap", width: 28 }}>
            {MODULE_SWATCH_ORDER.map((k) => (
              <Box
                key={k}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: def.moduleAccents![k][mode].main,
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              position: "absolute",
              left: 7,
              top: 9,
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: def.accent[mode].main,
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            left: 7,
            bottom: 8,
            right: 8,
            height: 3,
            borderRadius: 2,
            backgroundColor: def.neutral[mode].divider,
          }}
        />
      </Box>
      {selected && (
        <CheckIcon
          sx={{
            position: "absolute",
            right: 2,
            top: 2,
            fontSize: 13,
            color: def.accent[mode].main,
          }}
        />
      )}
    </Box>
  );
}

/** Six of the seven, spread around the wheel — enough to read as "many colours" at 8px. */
const MODULE_SWATCH_ORDER: ModuleKey[] = ["billing", "pharmacy", "radiology", "op", "lab", "ip"];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "text.secondary",
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

export function AppearanceMenu() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const themeId = useEffectiveThemeId();
  const setThemeId = useThemePref((s) => s.setThemeId);
  const mayOverride = useThemePref((s) => s.allowUserThemeOverride);
  const preference = useColorMode((s) => s.mode);
  const setMode = useColorMode((s) => s.setMode);
  const mode = useResolvedColorMode();
  const density = useDensity((s) => s.density);
  const setDensity = useDensity((s) => s.setDensity);

  return (
    <>
      <Tooltip title="Appearance">
        <IconButton
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Appearance settings"
          aria-haspopup="dialog"
          sx={{ minWidth: 48, minHeight: 48 }}
        >
          <PaletteOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // 360 leaves room for five 52px tiles plus gaps — the departmental theme lands in this row next.
        slotProps={{ paper: { sx: { mt: 1, p: 2, width: 360, borderRadius: 2 } } }}
      >
        <SectionLabel>Theme</SectionLabel>
        <Box sx={{ display: "flex", gap: 1, mb: mayOverride ? 2 : 1 }}>
          {THEME_ORDER.map((id) => (
            <Tooltip key={id} title={`${THEMES[id].label} — ${THEMES[id].blurb}`}>
              <Box
                component="button"
                type="button"
                disabled={!mayOverride}
                onClick={() => setThemeId(id)}
                aria-pressed={id === themeId}
                aria-label={THEMES[id].label}
                sx={{
                  border: 0,
                  padding: 0,
                  background: "none",
                  cursor: mayOverride ? "pointer" : "default",
                  // Not dimmed to unreadability: the point of showing a locked picker is that the
                  // current palette is still legible, so people can see which one is in force.
                  opacity: mayOverride ? 1 : 0.55,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  flex: 1,
                  minWidth: 0,
                  "&:focus-visible": { outline: "none", "& > :first-of-type": { outlineColor: "primary.main" } },
                }}
              >
                <ThemeTile themeId={id} mode={mode} selected={id === themeId} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    color: id === themeId ? "text.primary" : "text.secondary",
                    fontWeight: id === themeId ? 600 : 400,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortLabel(id)}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>

        {!mayOverride && (
          // Names the setting rather than just greying the control out, so an admin knows where to
          // go and a user knows this is policy, not a bug.
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Your organisation sets the palette for everyone. Light/dark and density are still yours.
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <SectionLabel>Mode</SectionLabel>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={preference}
          onChange={(_e, v: ColorModePreference | null) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="light"><LightModeIcon sx={{ fontSize: 16, mr: 0.75 }} />Light</ToggleButton>
          <ToggleButton value="dark"><DarkModeIcon sx={{ fontSize: 16, mr: 0.75 }} />Dark</ToggleButton>
          <ToggleButton value="system"><SettingsBrightnessIcon sx={{ fontSize: 16, mr: 0.75 }} />System</ToggleButton>
        </ToggleButtonGroup>

        <SectionLabel>Density</SectionLabel>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={density}
          onChange={(_e, v: Density | null) => v && setDensity(v)}
        >
          <ToggleButton value="comfortable"><DensityMediumIcon sx={{ fontSize: 16, mr: 0.75 }} />Comfortable</ToggleButton>
          <ToggleButton value="compact"><DensitySmallIcon sx={{ fontSize: 16, mr: 0.75 }} />Compact</ToggleButton>
        </ToggleButtonGroup>
      </Popover>
    </>
  );
}

/**
 * Tile captions. Registry labels are written for prose ("Enterprise Slate", "Departmental") and are
 * too long under a 52px tile, so a few are shortened to the word that actually distinguishes them.
 */
const SHORT_LABELS: Partial<Record<ThemeId, string>> = {
  slate: "Slate",
  departmental: "By module",
};
function shortLabel(id: ThemeId): string {
  return SHORT_LABELS[id] ?? THEMES[id].label;
}

